import React, { useState, useEffect } from 'react';
import axios from 'axios';

interface EmailVerificationProps {
  onVerified: () => void;
}

const EmailVerification: React.FC<EmailVerificationProps> = ({ onVerified }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [verificationToken, setVerificationToken] = useState('');

  useEffect(() => {
    // Check if there's a verification token in the URL
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    
    if (token) {
      setVerificationToken(token);
      verifyToken(token);
    }

    // Check if already verified in session
    checkVerificationStatus();
  }, []);

  useEffect(() => {
    if (timeRemaining > 0) {
      const timer = setTimeout(() => {
        setTimeRemaining(timeRemaining - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [timeRemaining]);

  const checkVerificationStatus = async () => {
    try {
      const response = await axios.get('/ai/api/check-verification.php', {
        withCredentials: true
      });
      
      if (response.data.verified) {
        onVerified();
      }
    } catch (error) {
      console.error('Error checking verification status:', error);
    }
  };

  const verifyToken = async (token: string) => {
    setIsLoading(true);
    setError('');
    
    try {
      const response = await axios.post('/ai/api/verify-token.php', {
        token
      }, {
        withCredentials: true
      });
      
      if (response.data.success) {
        setMessage('Email verified successfully! Redirecting...');
        setTimeout(() => {
          window.location.href = '/ai';
        }, 2000);
      } else {
        setError(response.data.message || 'Invalid verification token');
      }
    } catch (error) {
      setError('Failed to verify token. Please request a new verification email.');
    } finally {
      setIsLoading(false);
    }
  };

  const sendVerificationEmail = async () => {
    if (timeRemaining > 0) {
      return;
    }

    setIsLoading(true);
    setError('');
    setMessage('');
    
    try {
      const response = await axios.post('/ai/api/mailersend-verification.php', {}, {
        withCredentials: true
      });
      
      if (response.data.success) {
        setMessage('Verification email sent to mark_ramos@hotmail.co.uk');
        setTimeRemaining(60); // 60 seconds cooldown
      } else {
        setError(response.data.message || 'Failed to send verification email');
        if (response.data.timeRemaining) {
          setTimeRemaining(response.data.timeRemaining);
        }
      }
    } catch (error) {
      setError('Failed to send verification email. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-omega-darker via-omega-dark to-omega-primary flex items-center justify-center p-8">
      <div className="max-w-md w-full">
        <div className="bg-omega-dark/50 backdrop-blur-lg rounded-xl p-8 border border-omega-accent/30">
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-omega-accent to-green-400 mb-4">
              OMEGA AI
            </h1>
            <p className="text-xl text-gray-300">Email Verification Required</p>
          </div>

          <div className="space-y-6">
            <p className="text-gray-400 text-center">
              To access the Advanced AI Terminal Interface, please verify your email address.
            </p>

            {message && (
              <div className="bg-green-500/20 border border-green-500/50 rounded-lg p-4 text-green-400">
                {message}
              </div>
            )}

            {error && (
              <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-4 text-red-400">
                {error}
              </div>
            )}

            {!verificationToken && (
              <>
                <button
                  onClick={sendVerificationEmail}
                  disabled={isLoading || timeRemaining > 0}
                  className={`w-full py-3 font-bold rounded-lg transition-all ${
                    timeRemaining > 0
                      ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                      : 'bg-gradient-to-r from-omega-accent to-green-400 text-white hover:opacity-90 hover:scale-105 transform shadow-lg hover:shadow-omega-accent/30'
                  }`}
                >
                  {isLoading
                    ? 'Sending...'
                    : timeRemaining > 0
                    ? `Wait ${formatTime(timeRemaining)} before resending`
                    : 'Send Verification Email'}
                </button>

                <p className="text-sm text-gray-500 text-center">
                  A verification link will be sent to your registered email address.
                </p>
              </>
            )}

            {verificationToken && isLoading && (
              <div className="text-center">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-omega-accent"></div>
                <p className="text-gray-400 mt-4">Verifying your token...</p>
              </div>
            )}
          </div>
        </div>

        <div className="mt-4 text-center">
          <p className="text-sm text-gray-500">
            Powered by Omega Systems Technology
          </p>
        </div>
      </div>
    </div>
  );
};

export default EmailVerification;